import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from 'Components/ui/button';
import { Badge } from 'Components/ui/badge';
import { Card, CardContent } from 'Components/ui/card';
import {
    Phone,
    Mail,
    MapPin,
    Clock,
    CheckCircle,
    ArrowRight
} from 'lucide-react';
import ContactForm from 'Components/shared/ContactForm';

const contactInfo = [
    {
        icon: Phone,
        title: 'Telefono',
        value: '329 6139684',
        link: 'tel:+393296139684',
        description: 'Lun-Ven 8:00-18:00'
    },
    {
        icon: Mail,
        title: 'Email',
        value: 'info@tecnosys-ups.com',
        link: 'mailto:info@tecnosys-ups.com',
        description: 'Risposta entro 24h'
    },
    {
        icon: MapPin,
        title: 'Sede',
        value: 'Palermo, Sicilia',
        description: 'Operativi in tutta la Sicilia'
    },
    {
        icon: Clock,
        title: 'Orari',
        value: 'Lun-Ven 8:00-18:00',
        description: 'Emergenze H24'
    },
];

export default function Contatti() {
    const [submitted, setSubmitted] = useState(false);

    return (
        <div className="min-h-screen bg-white">
            {/* Hero */}
            <section className="relative bg-gray-900 pt-32 pb-20">
                <div className="absolute inset-0">
                    <img
                        src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=80"
                        alt="Edificio"
                        className="w-full h-full object-cover opacity-20"
                    />
                </div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <Badge className="bg-blue-600 text-white mb-6">Contattaci</Badge>
                        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
                            Siamo qui per aiutarti
                        </h1>
                        <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                            Richiedi un preventivo gratuito o parla con un nostro esperto
                            per trovare la soluzione perfetta per le tue esigenze.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Contact Info Cards */}
            <section className="py-12 -mt-12 relative z-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {contactInfo.map((info, index) => (
                            <motion.div
                                key={info.title}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <Card className="h-full hover:shadow-lg transition-shadow">
                                    <CardContent className="p-6 text-center">
                                        <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-blue-100 flex items-center justify-center">
                                            <info.icon className="w-6 h-6 text-blue-600" />
                                        </div>
                                        <h3 className="font-semibold text-gray-900 mb-1">{info.title}</h3>
                                        {info.link ? (
                                            <a
                                                href={info.link}
                                                className="text-blue-600 hover:text-blue-700 font-medium"
                                            >
                                                {info.value}
                                            </a>
                                        ) : (
                                            <p className="text-gray-900 font-medium">{info.value}</p>
                                        )}
                                        <p className="text-sm text-gray-500 mt-1">{info.description}</p>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Form Section */}
            <section className="py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-16">
                        {/* Form */}
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                        >
                            <h2 className="text-3xl font-bold text-gray-900 mb-2">Richiedi Informazioni</h2>
                            <p className="text-gray-600 mb-8">
                                Compila il form e riceverai una risposta entro 24 ore lavorative
                            </p>

                            {submitted ? (
                                <Card className="p-8 text-center">
                                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                                    <h3 className="text-xl font-semibold mb-2">Messaggio Inviato!</h3>
                                    <p className="text-gray-600 mb-6">
                                        Grazie per averci contattato. Ti risponderemo al più presto.
                                    </p>
                                    <Button
                                        onClick={() => setSubmitted(false)}
                                        variant="outline"
                                        className="rounded-full"
                                    >
                                        Invia un altro messaggio
                                    </Button>
                                </Card>
                            ) : (
                                <ContactForm showTitle={false} onSuccess={() => setSubmitted(true)} />
                            )}
                        </motion.div>

                        {/* Map & Info */}
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="space-y-8"
                        >
                            {/* Map */}
                            <div className="aspect-video rounded-2xl overflow-hidden bg-gray-100">
                                <iframe
                                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d199455.87924019!2d13.1918!3d38.1157!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1319e8c9b2f0488b%3A0x8e86f81b04c7b1e8!2sPalermo%2C%20PA!5e0!3m2!1sit!2sit!4v1699000000000!5m2!1sit!2sit"
                                    width="100%"
                                    height="100%"
                                    style={{ border: 0 }}
                                    allowFullScreen=""
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                    title="Mappa Palermo"
                                />
                            </div>

                            {/* Company Info */}
                            <Card>
                                <CardContent className="p-6 space-y-4">
                                    <h3 className="font-semibold text-gray-900 text-lg">Tecnosys di Lo Verso Salvatore</h3>
                                    <div className="space-y-2 text-gray-600">
                                        <p className="flex items-center gap-2">
                                            <MapPin className="w-4 h-4 text-blue-600" />
                                            Palermo, Sicilia - Operativi in tutta la regione
                                        </p>
                                        <p className="flex items-center gap-2">
                                            <Phone className="w-4 h-4 text-blue-600" />
                                            <a href="tel:+393296139684" className="hover:text-blue-600">329 6139684</a>
                                        </p>
                                        <p className="flex items-center gap-2">
                                            <Mail className="w-4 h-4 text-blue-600" />
                                            <a href="mailto:info@tecnosys-ups.com" className="hover:text-blue-600">info@tecnosys-ups.com</a>
                                        </p>
                                    </div>
                                    <div className="pt-4 border-t">
                                        <p className="text-sm text-gray-500">
                                            <strong>P.IVA:</strong> IT 05918540823
                                        </p>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* WhatsApp CTA */}
                            <a
                                href="https://wa.me/393296139684?text=Salve,%20vorrei%20informazioni%20sui%20vostri%20servizi%20UPS"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block"
                            >
                                <Card className="bg-green-500 hover:bg-green-600 transition-colors cursor-pointer">
                                    <CardContent className="p-6 flex items-center gap-4 text-white">
                                        <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                                        </svg>
                                        <div>
                                            <p className="font-semibold text-lg">Scrivici su WhatsApp</p>
                                            <p className="text-green-100 text-sm">Risposta rapida garantita</p>
                                        </div>
                                        <ArrowRight className="w-6 h-6 ml-auto" />
                                    </CardContent>
                                </Card>
                            </a>
                        </motion.div>
                    </div>
                </div>
            </section>
        </div>
    );
}